import type { Status } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { toEnum } from '@tryiris-ai/js-types';
import type { ReadonlyObjectDeep } from 'type-fest/source/readonly-deep.js';
import type { AgentStats } from './types.js';
import { Tab } from './types.js';

/**
 * Transforms the specified value to a `Tab` enumeration.
 *
 * @param value - The value to be transformed.
 * @returns The transformed `Tab` enumeration or `undefined`.
 */
export const toTab = (value: unknown): T_Maybe<Tab> => toEnum(Tab, value);

/**
 * Maps a Bridge's encoder status onto the stats the monitoring summary shows.
 *
 * The scalar disk figures describe the configured recording folder, as the
 * Bridge measures it. Total space and the recording stop limit are absent on
 * Bridges whose firmware predates them, and `-1` when the Bridge could not
 * measure them; callers treat both as unknown. The optional active snapshot
 * describes ongoing outputs independently when the configured folder changes.
 *
 * @param status - The Bridge's agent status.
 * @returns The agent stats.
 */
export const toAgentStats = (status: T_Maybe<ReadonlyObjectDeep<Status>>): AgentStats =>
  <AgentStats>{
    cpuUsage: status?.encoder?.cpuUsage
      ? (Math.round(status.encoder.cpuUsage * 100) / 100).toFixed(1)
      : undefined,
    ramUsage: status?.encoder?.memoryUsage
      ? (Math.round(status.encoder.memoryUsage * 100) / 100).toFixed(1)
      : undefined,
    operationMode: status?.encoder?.operationMode,
    diskSpace: status?.encoder?.diskFreeSpace,
    diskTotalSpace: status?.encoder?.diskTotalSpace,
    diskRecordingStopLimit: status?.encoder?.diskRecordingStopLimit,
    diskRecordingBlocked: status?.encoder?.diskRecordingBlocked,
    activeRecordingVolumes: status?.encoder?.activeRecordingVolumes,
    version: status?.encoder?.version
  };
